import { useState, useRef, type RefObject } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../auth/useAuth'
import { createProject, geocodeProject } from '../api/projects'
import type { ProjectCreate } from '../types'
import { Layout } from '../components/Layout'
import { Button } from '../components/Button'
import { Input } from '../components/Input'
import styles from './NewProjectPage.module.css'

const REGIONES = [
  'Arica y Parinacota', 'Tarapacá', 'Antofagasta', 'Atacama', 'Coquimbo',
  'Valparaíso', 'Metropolitana de Santiago', "O'Higgins", 'Maule',
  'Ñuble', 'Biobío', 'La Araucanía', 'Los Ríos', 'Los Lagos',
  'Aysén', 'Magallanes',
]

function scrollToFirst(ref: RefObject<HTMLElement | null>) {
  if (ref.current?.scrollIntoView) {
    ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }
  ref.current?.focus()
}

export function NewProjectPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const profileComplete = !!(user?.full_name && user?.license_number)
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const [form, setForm] = useState({
    name: '',
    address: '',
    commune: '',
    region: '',
    square_meters: '',
    dwelling_type: '',
    installation_type: '',
    normative_version: 'ric_2019',
  })

  const nameRef = useRef<HTMLInputElement>(null)
  const addressRef = useRef<HTMLInputElement>(null)
  const squareMetersRef = useRef<HTMLInputElement>(null)
  const dwellingTypeRef = useRef<HTMLSelectElement>(null)
  const regionRef = useRef<HTMLSelectElement>(null)

  const set = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }))

  const validate = (): boolean => {
    const errs: Record<string, string> = {}
    if (!form.name.trim()) errs.name = 'El nombre del proyecto es obligatorio.'
    if (!form.address.trim()) errs.address = 'La dirección es obligatoria.'
    const sqm = Number(form.square_meters)
    if (!form.square_meters || sqm < 1) errs.square_meters = 'La superficie debe ser al menos 1 m².'
    if (!form.dwelling_type) errs.dwelling_type = 'Selecciona el tipo de vivienda.'
    if (!form.region) errs.region = 'Selecciona la región.'
    setErrors(errs)

    // Scroll to first error in priority order
    if (errs.name) { scrollToFirst(nameRef); return false }
    if (errs.address) { scrollToFirst(addressRef); return false }
    if (errs.square_meters) { scrollToFirst(squareMetersRef); return false }
    if (errs.dwelling_type) { scrollToFirst(dwellingTypeRef); return false }
    if (errs.region) { scrollToFirst(regionRef); return false }

    return Object.keys(errs).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) return
    setLoading(true)
    try {
      const payload: ProjectCreate = {
        name: form.name.trim(),
        address: form.address.trim(),
        commune: form.commune || undefined,
        region: form.region,
        square_meters: Number(form.square_meters),
        dwelling_type: form.dwelling_type,
        installation_type: form.installation_type || undefined,
        normative_version: form.normative_version,
      }
      const project = await createProject(payload)
      try {
        await geocodeProject(project.id)
      } catch {
        // Silencioso — el usuario puede geocodificar manualmente después
      }
      navigate(`/projects/${project.id}`)
    } catch {
      setErrors({ form: 'No pudimos crear el proyecto. Intenta nuevamente.' })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Layout>
      <div className={styles.page}>
        <div className={styles.header}>
          <button className={styles.back} onClick={() => navigate('/')} aria-label="Volver">
            ← Volver
          </button>
          <h1 className={styles.title}>Nuevo proyecto</h1>
        </div>

        {profileComplete ? (
          <div className={styles.bannerInfo} role="note">
            ℹ️ Tus datos de instalador se completarán automáticamente desde tu perfil.
            Puedes editarlos después en el detalle del proyecto.{' '}
            <Link to="/profile" className={styles.bannerLink}>Actualizar perfil →</Link>
          </div>
        ) : (
          <div className={styles.bannerWarn} role="note">
            ⚠️ Completa tu perfil para que tus datos se auto-completen en cada proyecto.{' '}
            <Link to="/profile" className={styles.bannerLink}>Completar perfil</Link>
          </div>
        )}

        <form onSubmit={handleSubmit} className={styles.form} noValidate>
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Datos de la instalación</h2>

            <Input
              label="Nombre del proyecto"
              name="name"
              value={form.name}
              onChange={set('name')}
              placeholder="Casa Familia González"
              error={errors.name}
              required
              ref={nameRef}
            />

            <Input
              label="Dirección"
              name="address"
              value={form.address}
              onChange={set('address')}
              placeholder="Calle Los Pinos 123"
              error={errors.address}
              required
              ref={addressRef}
            />

            <Input
              label="Comuna"
              name="commune"
              value={form.commune}
              onChange={set('commune')}
              placeholder="Puerto Montt"
            />

            <div className={styles.field}>
              <label className={styles.label}>
                Región <span className={styles.req}>*</span>
              </label>
              <select
                className={[styles.select, errors.region ? styles.selectError : ''].join(' ')}
                value={form.region}
                onChange={set('region')}
                ref={regionRef}
              >
                <option value="">Selecciona una región</option>
                {REGIONES.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
              {errors.region && <span className={styles.error}>{errors.region}</span>}
            </div>

            <div className={styles.field}>
              <label className={styles.label}>
                Superficie <span className={styles.req}>*</span>
              </label>
              <div className={styles.inputWithSuffix}>
                <input
                  className={[styles.input, errors.square_meters ? styles.inputError : ''].join(' ')}
                  type="number"
                  name="square_meters"
                  value={form.square_meters}
                  onChange={set('square_meters')}
                  placeholder="85"
                  min="1"
                  ref={squareMetersRef}
                />
                <span className={styles.suffix}>m²</span>
              </div>
              {errors.square_meters && (
                <span className={styles.error}>{errors.square_meters}</span>
              )}
            </div>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Tipo de instalación</h2>

            <div className={styles.field}>
              <label className={styles.label}>
                Tipo de vivienda <span className={styles.req}>*</span>
              </label>
              <select
                className={[styles.select, errors.dwelling_type ? styles.selectError : ''].join(' ')}
                value={form.dwelling_type}
                onChange={set('dwelling_type')}
                ref={dwellingTypeRef}
              >
                <option value="">Selecciona...</option>
                <option value="casa">Casa</option>
                <option value="departamento">Departamento</option>
                <option value="local_comercial">Local comercial</option>
              </select>
              {errors.dwelling_type && <span className={styles.error}>{errors.dwelling_type}</span>}
            </div>

            <div className={styles.field}>
              <label className={styles.label}>Tipo de instalación</label>
              <select className={styles.select} value={form.installation_type} onChange={set('installation_type')}>
                <option value="">Selecciona...</option>
                <option value="nueva">Nueva</option>
                <option value="ampliacion">Ampliación</option>
                <option value="regularizacion">Regularización</option>
              </select>
            </div>

            <div className={styles.field}>
              <label className={styles.label}>Normativa</label>
              <select className={styles.select} value={form.normative_version} onChange={set('normative_version')}>
                <option value="ric_2019">DS 08/2019 RIC (recomendado)</option>
                <option value="nch4_2003">NCh Elec 4/2003 (legacy)</option>
              </select>
              <p className={styles.hint}>
                La normativa vigente es RIC DS 08/2019 desde julio 2021
              </p>
            </div>
          </section>

          {errors.form && (
            <p className={styles.formError} role="alert">{errors.form}</p>
          )}

          <div className={styles.geoNote}>
            <p>ℹ️ Las coordenadas geográficas se obtendrán automáticamente al crear el proyecto. Puedes verificarlas y corregirlas en el detalle del proyecto.</p>
          </div>

          <div className={styles.actions}>
            <Button type="submit" loading={loading} fullWidth>
              Crear proyecto
            </Button>
            <Button variant="secondary" fullWidth onClick={() => navigate('/')}>
              Cancelar
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  )
}
